import { useState, useEffect, useRef, useCallback } from "react";
import confetti from "canvas-confetti";
import Button from "@scripts/react/components/Button";
import { useWorkflowObjectName } from "@/hooks/useWorkflowObjectName";
import { useCurrentWorkflow } from "@/store";
import { useAuth } from "@/contexts/AuthContext";
import { useSignInAndCreateWorkflow } from "@/hooks/useSignInAndCreateWorkflow";

export const SuccessBanner = () => {
    const workflow = useCurrentWorkflow();
    const { pluralized: objectName } = useWorkflowObjectName();
    const { user } = useAuth();
    const { signInAndCreateWorkflow, loading } = useSignInAndCreateWorkflow();
    const confettiStorageKey = `rapidSuccessBannerConfetti${workflow?.id ?? ""}`;
    const bannerStorageKey = `rapidSuccessBannerClosed${workflow?.id ?? ""}`;
    const hasShownConfetti = useRef(Boolean(localStorage.getItem(confettiStorageKey)));
    const [shouldShow, setShouldShow] = useState(() => {
        // Initialize from localStorage - if banner was closed, don't show it
        return !Boolean(localStorage.getItem(bannerStorageKey));
    });

    useEffect(() => {
        if (!hasShownConfetti.current) {
            hasShownConfetti.current = true;
            localStorage.setItem(confettiStorageKey, "true");

            // Single burst of confetti
            const colors = ["#8B5CF6", "#A78BFA", "#C4B5FD"];

            confetti({ particleCount: 50, spread: 60, origin: { y: 0.3 }, colors: colors });
        }
    }, [confettiStorageKey]);

    const handleClose = useCallback(() => {
        setShouldShow(false);
        // Persist the closed state to localStorage
        localStorage.setItem(bannerStorageKey, "true");
    }, [bannerStorageKey]);

    return (
        shouldShow && (
            <div className="flex items-center justify-between rounded-xl border border-brand bg-gradient-to-b from-white to-purple-50 p-4">
                <div className="flex flex-col gap-1 text-base text-brand">
                    <span className="font-semibold">
                        {objectName
                            ? `Great work, you just built a computer vision model to identify ${objectName}!`
                            : "Great work, you just built a computer vision model in minutes!"}
                    </span>
                    <span className="font-normal">
                        {user ? (
                            "Start integrating it into your project or application."
                        ) : (
                            <>
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (!loading) {
                                            signInAndCreateWorkflow();
                                        }
                                    }}
                                    disabled={loading}
                                    className="hover:text-brand-dark font-medium text-brand underline disabled:cursor-not-allowed disabled:opacity-70"
                                >
                                    {loading ? "Signing in..." : "Sign in"}
                                </button>{" "}
                                to save your model and start integrating it into your project or
                                application.
                            </>
                        )}
                    </span>
                </div>
                {/* @ts-expect-error - Type mismatch between React type definitions */}
                <Button type="purbo-ghost" onClick={handleClose}>
                    <i className="far fa-close"></i>
                </Button>
            </div>
        )
    );
};
